import { useState } from 'react';
import { Activity, ChevronDown, ChevronUp, Plus, Trash2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { Id } from '../../../../convex/_generated/dataModel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useFitnessResultsForSession,
  useFitnessLatestPerPlayer,
  useBulkUpsertFitnessResults,
  useDeleteFitnessResult,
} from '@/hooks/use-fitness-results';

// ── Test type definitions ────────────────────────────────────────────

export interface FitnessTestDef {
  key: string;
  label: string;
  unit: string;
  /** true = higher value = better (beep, jumps, reps); false = lower = better (sprint, agility) */
  higherIsBetter: boolean;
  placeholder: string;
  /** How to display a stored value as a string */
  formatValue: (v: number) => string;
}

export const FITNESS_TESTS: FitnessTestDef[] = [
  {
    key: 'beep_test',
    label: 'Beep Test (20m Shuttle)',
    unit: 'level',
    higherIsBetter: true,
    placeholder: 'e.g. 8.4',
    formatValue: (v) => {
      const level = Math.floor(v);
      const shuttle = Math.round((v - level) * 10);
      return `${level}.${shuttle}`;
    },
  },
  {
    key: 'sprint_30m',
    label: 'Sprint – 30m',
    unit: 'seconds',
    higherIsBetter: false,
    placeholder: 'e.g. 4.23',
    formatValue: (v) => `${v.toFixed(2)}s`,
  },
  {
    key: 't_test',
    label: 'Agility – T-Test',
    unit: 'seconds',
    higherIsBetter: false,
    placeholder: 'e.g. 9.8',
    formatValue: (v) => `${v.toFixed(2)}s`,
  },
  {
    key: '4_corner',
    label: 'Agility – 4-Corner',
    unit: 'seconds',
    higherIsBetter: false,
    placeholder: 'e.g. 12.5',
    formatValue: (v) => `${v.toFixed(2)}s`,
  },
  {
    key: 'broad_jump',
    label: 'Standing Broad Jump',
    unit: 'cm',
    higherIsBetter: true,
    placeholder: 'e.g. 185',
    formatValue: (v) => `${v}cm`,
  },
  {
    key: 'vertical_jump',
    label: 'Vertical Jump',
    unit: 'cm',
    higherIsBetter: true,
    placeholder: 'e.g. 42',
    formatValue: (v) => `${v}cm`,
  },
  {
    key: 'pushups',
    label: 'Push-ups (1 min)',
    unit: 'reps',
    higherIsBetter: true,
    placeholder: 'e.g. 30',
    formatValue: (v) => `${v} reps`,
  },
  {
    key: 'situps',
    label: 'Sit-ups (1 min)',
    unit: 'reps',
    higherIsBetter: true,
    placeholder: 'e.g. 40',
    formatValue: (v) => `${v} reps`,
  },
  {
    key: 'cooper',
    label: 'Cooper Test (12 min)',
    unit: 'metres',
    higherIsBetter: true,
    placeholder: 'e.g. 2400',
    formatValue: (v) => `${v}m`,
  },
  {
    key: 'custom',
    label: 'Custom test…',
    unit: '',
    higherIsBetter: true,
    placeholder: 'Enter result',
    formatValue: (v) => String(v),
  },
];

function getTestDef(testType: string): FitnessTestDef {
  return FITNESS_TESTS.find((t) => t.key === testType) ?? FITNESS_TESTS[FITNESS_TESTS.length - 1];
}

// ── Delta display ────────────────────────────────────────────────────

function DeltaBadge({ current, previous, higherIsBetter }: { current: number; previous: number; higherIsBetter: boolean }) {
  const rawDelta = current - previous;
  if (rawDelta === 0) {
    return (
      <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const improved = higherIsBetter ? rawDelta > 0 : rawDelta < 0;
  const sign = rawDelta > 0 ? '+' : '';
  const displayDelta = Math.abs(rawDelta) < 1 ? rawDelta.toFixed(2) : rawDelta.toFixed(1);
  return (
    <span className={cn(
      'flex items-center gap-0.5 text-xs font-medium',
      improved ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
    )}>
      {improved
        ? <TrendingUp className="w-3 h-3" />
        : <TrendingDown className="w-3 h-3" />
      }
      {sign}{displayDelta}
    </span>
  );
}

// ── Component ────────────────────────────────────────────────────────

interface Player {
  id: string;
  firstName: string;
  lastName: string;
}

interface FitnessTestLogProps {
  sessionId: string;
  sessionDate: string; // "YYYY-MM-DD"
  players: Player[];
}

export function FitnessTestLog({ sessionId, sessionDate, players }: FitnessTestLogProps) {
  const { data: existingResults } = useFitnessResultsForSession(sessionId);
  const bulkUpsert = useBulkUpsertFitnessResults();
  const remove = useDeleteFitnessResult();

  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [selectedTestType, setSelectedTestType] = useState('beep_test');
  const [customTestName, setCustomTestName] = useState('');
  const [entries, setEntries] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const testDef = getTestDef(selectedTestType);

  // Fetch benchmarks for all session players for the selected test type
  const playerIds = players.map((p) => p.id);
  const { data: benchmarks } = useFitnessLatestPerPlayer(
    playerIds,
    selectedTestType,
    sessionDate,
  );

  const benchmarkMap = new Map(benchmarks.map((b) => [b.playerId, b]));

  // Group existing results by testType for display
  const resultsByTest = existingResults.reduce<Record<string, typeof existingResults>>((acc, r) => {
    const key = r.testType === 'custom' && r.customTestName ? r.customTestName : r.testType;
    (acc[key] ??= []).push(r);
    return acc;
  }, {});

  const handleSave = async () => {
    const results = players
      .filter((p) => entries[p.id]?.trim())
      .map((p) => ({ playerId: p.id, value: parseFloat(entries[p.id]) }))
      .filter((r) => !isNaN(r.value));

    if (results.length === 0) return;

    setSaving(true);
    try {
      await bulkUpsert.mutateAsync({
        sessionId,
        testType: selectedTestType,
        customTestName: selectedTestType === 'custom' ? customTestName.trim() : undefined,
        unit: testDef.unit,
        date: sessionDate,
        results,
      });
      setEntries({});
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const hasResults = existingResults.length > 0;
  const hasEntries = players.some((p) => entries[p.id]?.trim());

  return (
    <Card>
      <CardHeader className="pb-3">
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => setIsOpen((o) => !o)}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-4 h-4" />
            Fitness Tests
            {hasResults && (
              <span className="text-xs font-normal text-muted-foreground">
                ({existingResults.length})
              </span>
            )}
          </CardTitle>
          {isOpen
            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
          }
        </div>
      </CardHeader>

      {isOpen && (
        <CardContent className="space-y-3">
          {/* Existing results grouped by test */}
          {Object.entries(resultsByTest).map(([testKey, results]) => {
            const def = getTestDef(results[0].testType);
            const displayLabel = results[0].testType === 'custom' && results[0].customTestName
              ? results[0].customTestName
              : def.label;

            return (
              <div key={testKey} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {displayLabel}
                </p>
                <div className="space-y-1">
                  {results.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 border rounded-md px-3 py-2"
                    >
                      <span className="text-sm font-medium flex-1">{r.playerName}</span>
                      <span className="text-sm font-mono">{def.formatValue(r.value)}</span>
                      <button
                        onClick={() => remove.mutateAsync(r.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                        aria-label="Delete result"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {!hasResults && !showForm && (
            <p className="text-sm text-muted-foreground">No fitness tests recorded yet.</p>
          )}

          {/* Entry form */}
          {showForm ? (
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              {/* Test type selector */}
              <div className="space-y-1">
                <Label className="text-xs">Test type</Label>
                <select
                  value={selectedTestType}
                  onChange={(e) => {
                    setSelectedTestType(e.target.value);
                    setEntries({});
                  }}
                  className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {FITNESS_TESTS.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Custom test name */}
              {selectedTestType === 'custom' && (
                <div className="space-y-1">
                  <Label className="text-xs">Test name</Label>
                  <Input
                    placeholder="e.g. Plank hold (seconds)"
                    value={customTestName}
                    onChange={(e) => setCustomTestName(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}

              {/* Per-player result inputs */}
              <div className="space-y-2">
                <Label className="text-xs">
                  Results
                  {testDef.unit && (
                    <span className="text-muted-foreground font-normal ml-1">
                      — {testDef.unit}{!testDef.higherIsBetter ? ' (lower = better)' : ''}
                    </span>
                  )}
                </Label>
                {players.map((player) => {
                  const benchmark = benchmarkMap.get(player.id as Id<"players">);
                  const currentVal = parseFloat(entries[player.id] ?? '');
                  const showDelta = benchmark && !isNaN(currentVal);

                  return (
                    <div key={player.id} className="flex items-center gap-2">
                      <span className="text-sm w-32 shrink-0 truncate">
                        {player.firstName} {player.lastName}
                      </span>
                      <Input
                        type="number"
                        step="any"
                        placeholder={testDef.placeholder}
                        value={entries[player.id] ?? ''}
                        onChange={(e) =>
                          setEntries((prev) => ({ ...prev, [player.id]: e.target.value }))
                        }
                        className="text-sm w-28 shrink-0"
                      />
                      <div className="flex flex-col items-start min-w-0">
                        {benchmark && (
                          <span className="text-xs text-muted-foreground leading-tight">
                            prev: {testDef.formatValue(benchmark.value)}
                            <span className="ml-1 opacity-60">({benchmark.date})</span>
                          </span>
                        )}
                        {showDelta && (
                          <DeltaBadge
                            current={currentVal}
                            previous={benchmark!.value}
                            higherIsBetter={testDef.higherIsBetter}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || !hasEntries}
                  className="flex-1"
                >
                  {saving ? 'Saving…' : 'Save Results'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowForm(false); setEntries({}); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="w-full gap-1.5"
              onClick={() => setShowForm(true)}
            >
              <Plus className="w-3.5 h-3.5" />
              Record Fitness Test
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}
